import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const PROTECTED_PREFIXES = [
  '/dashboard', '/campaigns', '/safe-pages', '/reports', '/recordings',
  '/analytics', '/threats', '/threat-intel', '/advanced-protection',
  '/installation-guide', '/admin', '/referrals', '/sub-accounts',
  '/help', '/checkout', '/subscription',
];

const WAVE_POOL = 12;         // small pool — fewer composite layers
const EMIT_MIN_MS = 45;       // throttled emission
const EMIT_MIN_PX = 22;

export default function GoldenCursorTrail() {
  const { pathname } = useLocation();
  const isPublic = !PROTECTED_PREFIXES.some((p) => pathname.startsWith(p));

  const [mode, setMode] = useState(null);
  useEffect(() => {
    if (!isPublic) { setMode(null); return; }
    if (typeof window === 'undefined') return;
    // Respect reduced-motion preference
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setMode(null); return;
    }
    const hasHover = window.matchMedia && window.matchMedia('(hover: hover)').matches;
    const hasTouch = 'ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0;
    if (hasHover) setMode('mouse');
    else if (hasTouch) setMode('touch');
    else setMode(null);
  }, [isPublic]);

  const waveRefs = useRef(Array.from({ length: WAVE_POOL }, () => ({ current: null })));
  const primaryRef = useRef(null);

  useEffect(() => {
    if (!mode) return;

    let targetX = -9999, targetY = -9999;
    let lastEmitT = 0;
    let lastEmitX = -9999, lastEmitY = -9999;
    let nextSlot = 0;
    let hasInput = false;
    let rafId = 0;
    let pendingFrame = false;

    const emitWave = (px, py) => {
      const el = waveRefs.current[nextSlot].current;
      nextSlot = (nextSlot + 1) % WAVE_POOL;
      if (!el) return;
      el.style.left = px + 'px';
      el.style.top = py + 'px';
      el.style.animation = 'none';
      // eslint-disable-next-line no-unused-expressions
      el.offsetWidth;
      el.style.animation = 'wave-ripple 0.7s ease-out forwards';
    };

    const scheduleFrame = () => {
      if (pendingFrame) return;
      pendingFrame = true;
      rafId = requestAnimationFrame(() => {
        pendingFrame = false;
        if (primaryRef.current) {
          primaryRef.current.style.transform =
            `translate3d(${targetX}px, ${targetY}px, 0) translate(-50%, -50%)`;
          if (hasInput) primaryRef.current.style.opacity = '0.75';
        }
        const now = performance.now();
        const dist = Math.hypot(targetX - lastEmitX, targetY - lastEmitY);
        if (now - lastEmitT > EMIT_MIN_MS && dist > EMIT_MIN_PX) {
          emitWave(targetX, targetY);
          lastEmitT = now;
          lastEmitX = targetX;
          lastEmitY = targetY;
        }
      });
    };

    const setPointer = (px, py) => {
      targetX = px; targetY = py; hasInput = true;
      scheduleFrame();
    };
    const onMouseMove = (e) => setPointer(e.clientX, e.clientY);
    const onTouch = (e) => {
      const t = e.touches && e.touches[0];
      if (t) setPointer(t.clientX, t.clientY);
    };
    const hideGlow = () => {
      if (primaryRef.current) primaryRef.current.style.opacity = '0';
    };

    if (mode === 'mouse') {
      window.addEventListener('mousemove', onMouseMove, { passive: true });
    } else {
      window.addEventListener('touchstart', onTouch, { passive: true });
      window.addEventListener('touchmove', onTouch, { passive: true });
      window.addEventListener('touchend', hideGlow, { passive: true });
      window.addEventListener('touchcancel', hideGlow, { passive: true });
    }

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('touchstart', onTouch);
      window.removeEventListener('touchmove', onTouch);
      window.removeEventListener('touchend', hideGlow);
      window.removeEventListener('touchcancel', hideGlow);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [mode]);

  if (!mode) return null;

  // Soft golden radial gradient — the gradient itself provides the falloff,
  // so we DON'T need `filter: blur` or `mix-blend-mode: screen` (both of which
  // caused expensive repaints of text underneath).
  const glow = (size) => ({
    position: 'fixed',
    left: 0,
    top: 0,
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '50%',
    background:
      'radial-gradient(circle, rgba(255, 224, 130, 0.85) 0%, rgba(251, 191, 36, 0.55) 22%, rgba(245, 158, 11, 0.22) 48%, rgba(245, 158, 11, 0) 72%)',
    pointerEvents: 'none',
    willChange: 'transform, opacity',
    contain: 'layout paint',
    transformOrigin: 'center',
    transform: 'translate3d(-500px, -500px, 0)',
  });

  const primaryStyle = {
    ...glow(180),
    zIndex: 9999,
    opacity: 0,
    transition: 'opacity 0.2s ease',
  };

  const waveStyle = {
    ...glow(200),
    zIndex: 9998,
    opacity: 0,
  };

  return (
    <div data-testid="golden-cursor-trail" aria-hidden="true">
      {waveRefs.current.map((r, i) => (
        <div key={i} ref={(el) => { r.current = el; }} style={waveStyle} />
      ))}
      <div ref={primaryRef} style={primaryStyle} />
    </div>
  );
}
